/**
 * The Edit Expense window's own rules (piece 24; decision 1185): the draft
 * the window holds, drawn from the line it opened on, and the body its save
 * sends. 🔨 Pure, as every rule of the report is: the screen renders what these
 * answer and the service writes what the save carries.
 */
import { cents, formatCurrency, roundHalfUpCents } from '../money';
import { t, type StringKey } from '../strings';
import type { ExpenseFundingSourceValue } from './expenses';

/** A Company the window names, the merchant or the customer; a merchant typed with no Company carries a null id. */
export interface EntryCompany {
  id: string | null;
  name: string;
}

/** The Deal or the Work Order a line is for, with the name the field shows. */
export interface EntryProject {
  type: 'Deal' | 'WorkOrder';
  id: string;
  label: string;
}

/** The Item or the Site a stock line names, with the name the field shows. */
export interface EntryRef {
  id: string;
  label: string;
}

export interface EntryAttendee {
  personId: string;
  name: string;
  relationship: string;
}

/** One itemised line: its own category and its own amount, typed as the person typed it. */
export interface EntryItem {
  lineId: string | null;
  categoryId: string;
  amount: string;
}

/**
 * What the window holds while it is open. `amount` is the receipt's total as
 * typed; `items` is null until the person itemises, and then carries the
 * lines that sum to it (decision 1186).
 */
export interface ExpenseEntryDraft {
  reportId: string;
  incurredOn: string;
  merchant: EntryCompany | null;
  categoryId: string;
  amount: string;
  fundingSource: ExpenseFundingSourceValue | '';
  /** Decision 2961: the company card that paid, chosen under Paid With when Company card is; absent or empty names none. */
  companyCardId?: string;
  description: string;
  customer: EntryCompany | null;
  isBillable: boolean;
  project: EntryProject | null;
  attendees: EntryAttendee[];
  place: string;
  businessPurpose: string;
  items: EntryItem[] | null;
  /** Row T3407: what a category posting to 1200 requires beside the amount; absent reads as none chosen. */
  stockItem?: EntryRef | null;
  stockQuantity?: string;
  stockSite?: EntryRef | null;
}

/** The fields of a report line the window opens on; the report read carries every one of them. */
export interface EntryLineSource {
  id: string;
  version: number;
  incurredOn: string;
  /** Decision 1475: empty on a line the drop zone filed, until it is reviewed. */
  categoryId: string | null;
  description: string;
  amountCents: number;
  fundingSource: string | null;
  isBillable: boolean;
  merchantName: string | null;
  place: string | null;
  businessPurpose: string | null;
  mileageMiles: number | null;
  attendees: { personId: string; name: string; relationship: string }[];
  receipt: { id: string; filename: string } | null;
  merchantCompany: { id: string; name: string } | null;
  customerCompany: { id: string; name: string } | null;
  project: { type: string; id: string; label: string } | null;
  /** Decision 2961: the card the line was paid with, and whether it is still active. */
  companyCard?: { id: string; name: string; isActive: boolean } | null;
  /** Row T3407: the item, quantity and site a line in a 1200 category carries. */
  item?: EntryRef | null;
  quantity?: number | null;
  site?: EntryRef | null;
}

/** Whole cents as the plain decimal a person types, with no symbol: the field's own text, never a rendering. */
export function centsAsTyped(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const whole = Math.abs(cents);
  return `${sign}${Math.floor(whole / 100)}.${String(whole % 100).padStart(2, '0')}`;
}

/** The cents a typed amount carries, or null where it is blank or not a number. */
export function typedCents(text: string): number | null {
  const trimmed = text.trim().replace(/^\$/, '').replace(/,/g, '');
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? roundHalfUpCents(value * 100) : null;
}

/**
 * 🔒 The lines one window edits together: the line it opened on and every
 * other line of the same report naming the same receipt (decision 1186). A
 * line with no receipt stands alone.
 */
export function entryGroup<T extends { id: string; receipt: { id: string } | null }>(lines: readonly T[], lineId: string): T[] {
  const opened = lines.find((line) => line.id === lineId);
  if (!opened) return [];
  if (!opened.receipt) return [opened];
  return lines.filter((line) => line.receipt?.id === opened.receipt!.id);
}

/** An empty draft, for a receipt with nothing on it yet; the report is the one the walk stands on, where it has one. */
export function emptyEntryDraft(reportId: string, today: string): ExpenseEntryDraft {
  return {
    reportId, incurredOn: today, merchant: null, categoryId: '', amount: '', fundingSource: '', description: '',
    customer: null, isBillable: false, project: null, attendees: [], place: '', businessPurpose: '', items: null,
  };
}

/** The draft a window opened on a line starts from: the group's shared fields, and its lines as items where there are several. */
export function entryDraftFrom(reportId: string, group: readonly EntryLineSource[]): ExpenseEntryDraft {
  const first = group[0]!;
  const total = group.reduce((sum, line) => sum + line.amountCents, 0);
  const merchant: EntryCompany | null = first.merchantCompany ? { id: first.merchantCompany.id, name: first.merchantCompany.name } : first.merchantName ? { id: null, name: first.merchantName } : null;
  return {
    reportId,
    incurredOn: first.incurredOn,
    merchant,
    categoryId: first.categoryId ?? '',
    amount: centsAsTyped(total),
    fundingSource: first.fundingSource === 'CompanyCard' || first.fundingSource === 'Reimbursable' ? first.fundingSource : '',
    description: first.description,
    customer: first.customerCompany ? { id: first.customerCompany.id, name: first.customerCompany.name } : null,
    isBillable: first.isBillable,
    project: first.project && (first.project.type === 'Deal' || first.project.type === 'WorkOrder') ? { type: first.project.type, id: first.project.id, label: first.project.label } : null,
    attendees: first.attendees.map((row) => ({ personId: row.personId, name: row.name, relationship: row.relationship })),
    place: first.place ?? '',
    businessPurpose: first.businessPurpose ?? '',
    ...(first.item ? { stockItem: first.item } : {}),
    ...(first.quantity !== null && first.quantity !== undefined ? { stockQuantity: String(first.quantity) } : {}),
    ...(first.site ? { stockSite: first.site } : {}),
    items: group.length > 1 ? group.map((line) => ({ lineId: line.id, categoryId: line.categoryId ?? '', amount: centsAsTyped(line.amountCents) })) : null,
    ...(first.companyCard ? { companyCardId: first.companyCard.id } : {}),
  };
}

/**
 * 🔒 What an itemised receipt still leaves unassigned (decision 1186): the
 * receipt's total less the itemised lines, in cents, and zero when they meet.
 * A blank or unreadable amount counts as nothing, so the remainder shows the
 * whole of what is still to place.
 */
export function itemiseRemainderCents(total: string, items: readonly { amount: string }[]): number {
  const sum = items.reduce((running, item) => running + (typedCents(item.amount) ?? 0), 0);
  return (typedCents(total) ?? 0) - sum;
}

/**
 * 🔨 The refusal a remainder earns, naming it (decision 1186): itemised lines
 * sum to the receipt's total, and a save that leaves money unplaced or places
 * too much is refused with the amount it is out by. Null when they meet.
 */
export function itemiseProblem(totalCents: number, itemCents: readonly number[]): string | null {
  const remainder = totalCents - itemCents.reduce((sum, amount) => sum + amount, 0);
  if (remainder === 0) return null;
  const total = formatCurrency(cents(totalCents));
  const gap = formatCurrency(cents(Math.abs(remainder)));
  return remainder > 0
    ? `The itemized lines leave ${gap} of the receipt’s ${total} unassigned; add a line for it or correct an amount.`
    : `The itemized lines come to ${gap} more than the receipt’s ${total}; correct an amount so they sum to the total.`;
}

/** The two lines a receipt opens into when the person presses Itemise: what the one line held, and a blank second. */
export function startItemising(draft: ExpenseEntryDraft, openedLineId: string | null): EntryItem[] {
  return [
    { lineId: openedLineId, categoryId: draft.categoryId, amount: draft.amount },
    { lineId: null, categoryId: '', amount: '' },
  ];
}

/**
 * 🔒 The value the Report field holds while the expense is to stand on no
 * report (decision 1313). It never reaches the server: the save sends no
 * report, and the line is Unreported, its filer's own.
 */
export const UNREPORTED_EXPENSE = 'unreported';

/** What the save sends (decision 1185): one receipt's lines and the fields they share; no report is an unreported expense (decision 1313). */
export interface ExpenseEntryBody {
  reportId: string | null;
  receiptFileId: string | null;
  incurredOn: string;
  merchantCompanyId: string | null;
  merchantName: string | null;
  customerCompanyId: string | null;
  isBillable: boolean;
  projectType: 'Deal' | 'WorkOrder' | null;
  projectId: string | null;
  fundingSource: ExpenseFundingSourceValue;
  /** Decision 2961: sent only on a Company card expense that names its card; a line sent without it names none. */
  companyCardId?: string;
  description: string;
  place: string | null;
  businessPurpose: string | null;
  attendees: { personId: string; relationship: string }[];
  /** Row T3407: sent only when a category of the expense posts to 1200. */
  itemId?: string;
  quantity?: number;
  siteId?: string;
  items: { lineId: string | null; categoryId: string; amountCents: number }[];
  /** The receipt's total, which itemised lines sum to (decision 1186). */
  totalCents: number;
  replaces: { id: string; version: number }[];
}

/**
 * The body a draft saves as, or the sentence saying what is still missing.
 * 🔨 The window's own refusals are the ones a person can fix before the round
 * trip; the service still refuses everything the report's rules refuse.
 */
export function entryBodyFrom(
  draft: ExpenseEntryDraft,
  context: { receiptFileId: string | null; openedLines: readonly { id: string; version: number }[]; stock?: boolean },
): { body: ExpenseEntryBody } | { problem: string } {
  if (!draft.reportId) return { problem: 'Choose the report this expense is filed on.' };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(draft.incurredOn)) return { problem: 'Enter the date the money was spent.' };
  if (!draft.fundingSource) return { problem: 'Say whose money paid for it, the company card or your own.' };
  if (!draft.description.trim()) return { problem: 'Say what the expense was for.' };
  const total = typedCents(draft.amount);
  if (total === null || total === 0) return { problem: 'Enter the amount on the receipt.' };
  const opened = context.openedLines;
  let items: ExpenseEntryBody['items'];
  if (draft.items === null) {
    if (!draft.categoryId) return { problem: 'Choose the category the expense belongs to.' };
    items = [{ lineId: opened[0]?.id ?? null, categoryId: draft.categoryId, amountCents: total }];
  } else {
    items = [];
    for (const item of draft.items) {
      const itemCents = typedCents(item.amount);
      if (!item.categoryId) return { problem: 'Choose a category on every itemized line.' };
      if (itemCents === null || itemCents === 0) return { problem: 'Enter an amount on every itemized line.' };
      items.push({ lineId: item.lineId, categoryId: item.categoryId, amountCents: itemCents });
    }
    const remainder = itemiseProblem(total, items.map((item) => item.amountCents));
    if (remainder) return { problem: remainder };
  }
  // Row T3407: a category posting to 1200 names its item, quantity and site, each refused by name before the round trip.
  let stock: Pick<ExpenseEntryBody, 'itemId' | 'quantity' | 'siteId'> = {};
  if (context.stock) {
    const quantity = Number(draft.stockQuantity?.trim() ?? '');
    if (!draft.stockItem) return { problem: t('expenseEntry.stockItemNeeded') };
    if (!Number.isFinite(quantity) || quantity <= 0) return { problem: t('expenseEntry.stockQuantityNeeded') };
    if (!draft.stockSite) return { problem: t('expenseEntry.stockSiteNeeded') };
    stock = { itemId: draft.stockItem.id, quantity, siteId: draft.stockSite.id };
  }
  return {
    body: {
      ...stock,
      reportId: draft.reportId === UNREPORTED_EXPENSE ? null : draft.reportId,
      receiptFileId: context.receiptFileId,
      incurredOn: draft.incurredOn,
      merchantCompanyId: draft.merchant?.id ?? null,
      merchantName: draft.merchant?.name.trim() || null,
      customerCompanyId: draft.customer?.id ?? null,
      isBillable: draft.isBillable,
      projectType: draft.project?.type ?? null,
      projectId: draft.project?.id ?? null,
      fundingSource: draft.fundingSource,
      description: draft.description.trim(),
      place: draft.place.trim() || null,
      businessPurpose: draft.businessPurpose.trim() || null,
      attendees: draft.attendees.map((row) => ({ personId: row.personId, relationship: row.relationship.trim() })),
      items,
      totalCents: total,
      replaces: opened.map((line) => ({ id: line.id, version: line.version })),
      ...(draft.fundingSource === 'CompanyCard' && draft.companyCardId ? { companyCardId: draft.companyCardId } : {}),
    },
  };
}

// ---- + New Expense's report (decision 1311) ----

/**
 * 🔒 The value the Report field holds while + New Expense stands on a report
 * the save will make (decision 1311). It never reaches the server: the window
 * makes the report first and files the expense onto the id it answers.
 */
export const NEW_EXPENSE_REPORT = 'new-report';

/** The period a report + New Expense makes covers: the calendar month the day falls in, first day to last. */
export function expensePeriodFor(today: string): { periodStart: string; periodEnd: string } {
  const [year, month] = today.split('-').map(Number) as [number, number];
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const monthText = String(month).padStart(2, '0');
  return { periodStart: `${year}-${monthText}-01`, periodEnd: `${year}-${monthText}-${String(lastDay).padStart(2, '0')}` };
}

/**
 * 🔒 The report + New Expense files onto by default (decision 1311): the
 * person's own Draft report whose period covers the day, the latest-starting
 * where several do, and null where none does, which is when the save makes one.
 */
export function openDraftReportFor<T extends { id: string; status: string; periodStart: string; periodEnd: string }>(rows: readonly T[], today: string): T | null {
  const covering = rows.filter((row) => row.status === 'Draft' && row.periodStart <= today && today <= row.periodEnd);
  covering.sort((a, b) => (a.periodStart < b.periodStart ? 1 : a.periodStart > b.periodStart ? -1 : 0));
  return covering[0] ?? null;
}

// ---- the receipt's read fields (decision 1188) ----

/** The fields a receipt's reading prefills: the three the reference names, and nothing else. */
export type ReadField = 'incurredOn' | 'merchant' | 'amount';

/**
 * How a receipt's reading came out (T741): `read` where the reader found at
 * least one of the three, `noReader` where no reader for a picture is
 * configured, and `failed` where it was asked and found nothing or broke.
 */
export type ReceiptReadingStatus = 'read' | 'noReader' | 'failed';

/** What the intake seam's reading of a receipt offers the window; each is null where the reader did not find it. */
export interface ReceiptReading {
  /** 🔒 Said, never left to an empty reading: a pane that fills nothing says why (T741). */
  status: ReceiptReadingStatus;
  incurredOn: string | null;
  merchantName: string | null;
  amountCents: number | null;
  /** The Company whose name the merchant reads as, where one matches it exactly; null offers quick-create instead. */
  merchantCompany: { id: string; name: string } | null;
}

const TOTAL_LINE = /\b(?:grand\s+total|total\s+due|amount\s+due|balance\s+due|total)\b/i;
const SUBTOTAL_LINE = /\bsub[\s-]*total\b/i;
const AMOUNT_ON_LINE = /\$?\s*(\d{1,3}(?:,\d{3})+|\d+)\.(\d{2})\b/g;

/**
 * 🔒 The merchant and the total a receipt's text says (decision 1188). The
 * merchant is the first line carrying a word and no amount, which is where a
 * till prints its own name; the total is the last amount on the last line labelled a total and
 * not a subtotal, which is where a till prints what was paid. 🔨 Either is null
 * where the text does not say it plainly: a guess is never offered as a read.
 */
export function receiptFactsFromText(text: string): { merchantName: string | null; totalCents: number | null } {
  const lines = text.split(/\r?\n/).map((row) => row.trim()).filter((row) => row !== '');
  // A line carrying an amount or a total's label is the till's arithmetic, never its name.
  const named = lines.find((row) => /[A-Za-z]{2,}/.test(row) && !/^(receipt|invoice|order)\b/i.test(row) && !TOTAL_LINE.test(row) && !SUBTOTAL_LINE.test(row) && !/\d\.\d{2}\b/.test(row));
  const merchantName = named && named.length <= 80 ? named.replace(/\s{2,}/g, ' ') : null;
  let totalCents: number | null = null;
  for (const row of lines) {
    if (!TOTAL_LINE.test(row) || SUBTOTAL_LINE.test(row)) continue;
    const amounts = Array.from(row.matchAll(AMOUNT_ON_LINE));
    const last = amounts[amounts.length - 1];
    if (last) totalCents = Number(last[1]!.replace(/,/g, '')) * 100 + Number(last[2]);
  }
  return { merchantName, totalCents };
}

/**
 * 🔒 A reading laid over the draft (decision 1188): each read field fills only
 * where the person has not touched it, and the fields it filled are marked as
 * read rather than typed. A field the reading did not find is left as it was.
 */
export function applyReceiptReading(
  draft: ExpenseEntryDraft,
  reading: ReceiptReading,
  touched: ReadonlySet<ReadField>,
): { draft: ExpenseEntryDraft; read: ReadField[] } {
  const next = { ...draft };
  const read: ReadField[] = [];
  if (reading.incurredOn && !touched.has('incurredOn')) {
    next.incurredOn = reading.incurredOn;
    read.push('incurredOn');
  }
  if ((reading.merchantCompany || reading.merchantName) && !touched.has('merchant')) {
    next.merchant = reading.merchantCompany ? { id: reading.merchantCompany.id, name: reading.merchantCompany.name } : { id: null, name: reading.merchantName! };
    read.push('merchant');
  }
  if (reading.amountCents !== null && reading.amountCents > 0 && !touched.has('amount') && draft.items === null) {
    next.amount = centsAsTyped(reading.amountCents);
    read.push('amount');
  }
  return { draft: next, read };
}

// ---- Add to Report (decision 1314) ----

/** One report Add to Report put expenses on, as the service answers it. */
export interface AddedToReport {
  reportId: string;
  reportNumber: string;
  periodStart: string;
  periodEnd: string;
  /** True where Add to Report made the report because none of the person's Draft reports covered the expense. */
  made: boolean;
  lineCount: number;
}

/**
 * 🔒 Where Add to Report puts each unreported expense (decision 1314): on the
 * person's own Draft report whose period covers the expense's day, the
 * latest-starting where several do, and where none does, on one report for
 * that calendar month. 🔨 Expenses in the same month with no report share the
 * one report made for it, so a press makes one report per month and never two.
 */
export function addToReportPlan<R extends { id: string; status: string; periodStart: string; periodEnd: string }>(
  lines: readonly { id: string; incurredOn: string }[],
  reports: readonly R[],
): { onto: { reportId: string; lineIds: string[] }[]; toMake: { periodStart: string; periodEnd: string; lineIds: string[] }[] } {
  const onto = new Map<string, string[]>();
  const toMake = new Map<string, { periodStart: string; periodEnd: string; lineIds: string[] }>();
  for (const line of lines) {
    const open = openDraftReportFor(reports, line.incurredOn);
    if (open) {
      onto.set(open.id, [...(onto.get(open.id) ?? []), line.id]);
      continue;
    }
    const period = expensePeriodFor(line.incurredOn);
    const month = toMake.get(period.periodStart) ?? { ...period, lineIds: [] };
    month.lineIds.push(line.id);
    toMake.set(period.periodStart, month);
  }
  return {
    onto: [...onto].map(([reportId, lineIds]) => ({ reportId, lineIds })),
    toMake: [...toMake.values()].sort((a, b) => a.periodStart.localeCompare(b.periodStart)),
  };
}

/** What the page says after Add to Report (decision 1314): each report the expenses went on, and each one made, by number and period. */
export function addToReportNotice(placed: readonly AddedToReport[]): string {
  const count = (n: number) => (n === 1 ? 'One expense was' : `${n} expenses were`);
  return placed
    .map((row) => (row.made
      ? `${row.reportNumber} was made for ${row.periodStart} to ${row.periodEnd}, and ${count(row.lineCount).toLowerCase()} added to it.`
      : `${count(row.lineCount)} added to ${row.reportNumber}.`))
    .join(' ');
}

// ---- the receipt pane's states (T741) ----

/**
 * 🔒 What the receipt pane says it is waiting for, at every step (T741; the
 * owner's report of 2026-10-01: *"i added a reciept to the new expense it does
 * not upload and show on the window and the info is not auto populated."*).
 * The pane was silent while a picked receipt waited on its File Type and while
 * a reading filled nothing, so a person could not tell a wait from a fault.
 */
export type ReceiptPaneState = 'typeNeeded' | 'uploading' | 'uploadFailed' | 'uploaded' | 'reading' | 'read' | 'noReader' | 'readFailed';

/** The string key each state is said in, through the one lookup. */
export const RECEIPT_PANE_TEXT: Readonly<Record<ReceiptPaneState, StringKey>> = {
  typeNeeded: 'expenseEntry.state.typeNeeded',
  uploading: 'expenseEntry.uploading',
  uploadFailed: 'expenseEntry.state.uploadFailed',
  uploaded: 'expenseEntry.state.uploaded',
  reading: 'expenseEntry.state.reading',
  read: 'expenseEntry.state.read',
  noReader: 'expenseEntry.state.noReader',
  readFailed: 'expenseEntry.state.readFailed',
};

/**
 * The state the pane stands in. `upload` is where the picked file is: none
 * picked, waiting on its type, sending, refused, or landed. `reading` is the
 * read of the landed receipt: not asked, on its way, answered with its status,
 * or not answered at all, which reads as failed.
 */
export function receiptPaneState(input: {
  upload: 'none' | 'waitingOnType' | 'sending' | 'failed' | 'landed';
  reading: 'notAsked' | 'loading' | ReceiptReadingStatus | 'unanswered';
}): ReceiptPaneState | null {
  switch (input.upload) {
    case 'none': return null;
    case 'waitingOnType': return 'typeNeeded';
    case 'sending': return 'uploading';
    case 'failed': return 'uploadFailed';
    default: break;
  }
  switch (input.reading) {
    case 'notAsked': return 'uploaded';
    case 'loading': return 'reading';
    case 'read': return 'read';
    case 'noReader': return 'noReader';
    default: return 'readFailed';
  }
}

// ---- the Expenses page's drop zone (decision 1475) ----

/**
 * 🔒 What one receipt the drop zone sent became (decision 1475; T744): filed as
 * an unreported line marked Needs review, with what its reading found, or not
 * filed because it matches a line of the person's own on merchant, amount and
 * date, in which case it waits in the Documents Inbox.
 */
export type ScannedReceipt =
  | { outcome: 'filed'; lineId: string; reading: ReceiptReadingStatus; merchantName: string | null; amountCents: number | null; incurredOn: string }
  | { outcome: 'duplicate'; reading: ReceiptReadingStatus; duplicateOf: { id: string; merchantName: string; amountCents: number; incurredOn: string; description: string } };
